const GOOGLE_MAPS_SCRIPT_ID = 'bhg-google-maps-js';

let loadPromise: Promise<void> | null = null;

function isGoogleMapsReady(): boolean {
  return Boolean(
    window.google?.maps?.Map && window.google?.maps?.marker?.AdvancedMarkerElement
  );
}

/**
 * Load Maps JavaScript API once (marker library for AdvancedMarkerElement).
 * Reuses the same script tag across navigations.
 */
export function loadGoogleMapsJsApi(apiKey: string): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is only available in the browser'));
  }
  if (isGoogleMapsReady()) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const callbackName = '__bhgMapsReady';

    const finish = () => {
      delete (window as unknown as Record<string, unknown>)[callbackName];
      if (isGoogleMapsReady()) {
        resolve();
      } else {
        loadPromise = null;
        reject(new Error('Google Maps marker library failed to load'));
      }
    };

    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (isGoogleMapsReady()) {
        resolve();
        return;
      }
      (window as unknown as Record<string, unknown>)[callbackName] = finish;
      return;
    }

    (window as unknown as Record<string, unknown>)[callbackName] = finish;

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.onerror = () => {
      delete (window as unknown as Record<string, unknown>)[callbackName];
      loadPromise = null;
      reject(new Error('Google Maps failed to load'));
    };
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&libraries=marker&callback=${callbackName}`;
    document.head.appendChild(script);
  });

  return loadPromise;
}
